import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronRight, Layers } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface AssessmentSummary {
  id: string;
  title: string;
  description: string;
  slug: string;
  is_maturity_model: boolean;
}

interface AssessmentSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (assessmentId: string, slug: string) => void;
}

export default function AssessmentSelectorDialog({ open, onOpenChange, onSelect }: AssessmentSelectorDialogProps) {
  const [assessments, setAssessments] = useState<AssessmentSummary[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) fetchAssessments();
  }, [open]);

  const fetchAssessments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('assessments')
        .select('id, title, description, slug, is_maturity_model')
        .eq('is_active', true)
        .eq('status', 'published');

      if (error) throw error;
      setAssessments(data || []);
    } catch (err) {
      console.error('Error fetching assessments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (assessment: AssessmentSummary) => {
    onOpenChange(false);
    onSelect(assessment.id, assessment.slug);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select an Assessment</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading assessments...</div>
          ) : assessments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Layers className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p>No active assessments available.</p>
            </div>
          ) : (
            assessments.map((assessment) => (
              <Card
                key={assessment.id}
                className="cursor-pointer hover:border-primary/30 transition-colors"
                onClick={() => handleSelect(assessment)}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-foreground">{assessment.title}</h3>
                    {assessment.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{assessment.description}</p>
                    )}
                    {assessment.is_maturity_model && (
                      <Badge variant="secondary" className="text-xs mt-2">Maturity Model</Badge>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 ml-3" />
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
